import qs from 'query-string';
import { mergeQueryParamsToUrl } from '@shared/utils/urlHelpers';
import { useCallback } from 'react';
import { useQueryParamHelper } from '@shared/core/queryString';

enum TRANSFERRED_STAYS_QUERY_PARAMS {
  discountCode = 'discountCode',
}

interface UseRetainSupportedStaysParams {
  retainSupportedStaysParams: (targetUrl: string) => string;
}

/**
 * A custom hook that retains supported query parameters (e.g., `discountCode`)
 * from the current URL and merges them into any target URL.
 *
 * @returns {UseRetainSupportedStaysParams} An object containing a function to enhance URLs with retained query parameters.
 */
export const useRetainSupportedStaysParams = (): UseRetainSupportedStaysParams => {
  const queryParamHelper = useQueryParamHelper();
  const discountCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.discountCode);

  const retainSupportedStaysParams = (targetUrl: string): string => {
    return mergeQueryParamsToUrl(targetUrl, { discountCode });
  };

  return { retainSupportedStaysParams };
};

interface UseRetainSupportedParamsInRedirectUrl {
  retainSupportedParamsInStaysRedirectUrl: (targetUrl: string) => string;
}

/**
 * Hook for enhancing redirect URLs by retaining supported query parameters from the current Stays page.
 *
 * Currently, only the `discountCode` query parameter is retained.
 * If the destination URL (inside the `destination` param) has `idType=stays`,
 * the `discountCode` is injected into it.
 *
 * @returns {UseRetainSupportedParamsInRedirectUrl} Object containing a function to enhance redirect URLs.
 */
export const useRetainSupportedParamsInStaysRedirectUrl = (): UseRetainSupportedParamsInRedirectUrl => {
  const queryParamHelper = useQueryParamHelper();
  const discountCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.discountCode);

  const retainSupportedParamsInStaysRedirectUrl = useCallback(
    (redirectUrl: string): string => {
      if (!redirectUrl || !discountCode) return redirectUrl;

      // Parse outer URL (e.g. https://example.com/?destination=...)
      const { url: baseUrl, query: outerQuery, fragmentIdentifier: outerFragment } = qs.parseUrl(redirectUrl, { parseFragmentIdentifier: true });

      const destinationEncoded = outerQuery.destination as string | undefined;
      if (!destinationEncoded) return redirectUrl;

      // Decode and parse the destination URL (e.g. /search?...&idType=stays)
      const destinationDecoded = decodeURIComponent(destinationEncoded);
      const { url: innerUrl, query: innerQuery, fragmentIdentifier: innerFragment } = qs.parseUrl(destinationDecoded, { parseFragmentIdentifier: true });

      // Only modify the destination if it's a Stays URL
      if (innerQuery.idType !== 'stays') return redirectUrl;

      // Inject supported params into the decoded destination URL
      const updatedDestination = qs.stringifyUrl(
        {
          url: innerUrl,
          query: {
            ...innerQuery,
            discountCode,
          },
          fragmentIdentifier: innerFragment,
        },
        {
          skipEmptyString: true,
          skipNull: true,
          arrayFormat: 'comma',
        },
      );

      // Re-encode the destination and return the updated outer URL
      return qs.stringifyUrl(
        {
          url: baseUrl,
          query: { ...outerQuery, destination: updatedDestination },
          fragmentIdentifier: outerFragment,
        },
        {
          skipEmptyString: true,
          skipNull: true,
        },
      );
    },
    [discountCode],
  );

  return { retainSupportedParamsInStaysRedirectUrl };
};
